import { default as useCommerceBasket } from '~/features/commerce-api/hooks/useBasket';
import { useCallback, useEffect } from 'react';
import create from 'zustand';
import { Basket, Product, ProductItemBasket, Variant } from '~/lib/data-contract/salesforce';
import { useEvents } from '~/shared/hooks/useEvents';
import { _transformProductToDataLayer } from '~/shared/hooks/useEvents/helpers';
import { ProductEventData } from '~/shared/hooks/useEvents/models';
import { useFrame } from '~/shared/utils';
import { useErrorHandler } from '~/shared/utils/errorBoundary/hooks/useErrorHandler';
import { useTranslation } from '~/shared/utils/translation';
import { useBasketState } from './useBasketState';
import { _matchCouponsFromCartWithProducts } from '~/shared/hooks/useEvents/useGTMEvents/helper';
import { StorePickupOption } from '~/shared/hooks/useEvents/useGTMEvents/models';
import { notifyError } from '~/shared/utils/errorBoundary/toast';
import useCustomer from '~/features/commerce-api/hooks/useCustomer';

interface BasketStore {
    basket: Basket | undefined;
    isInitialized: boolean;
    setBasket: (basket: Basket) => void;
    setInitialized: (isInitialized: boolean) => void;
}

import {
    BasketErrorType,
    ErrorResponse,
    isErrorResponse,
    BasketOperationQueue,
    createBasketErrorHandler,
    mapApiErrorToBasketError,
} from '../utils/basketOperations';
import { useCommerceAPI } from '~/features/commerce-api/contexts';

export enum QuantityChangeType {
    ADD = 'ADD',
    REMOVE = 'REMOVE',
}
export const useBasketStore = create<BasketStore>((set) => ({
    basket: undefined,
    isInitialized: false,
    setBasket: (basket: Basket) => set({ basket }),
    setInitialized: (isInitialized: boolean) => set({ isInitialized }),
}));
/**
 * Exposes basket data and actions.
 */
interface BasketOptions {
    currency?: string;
}

export const useBasket = (opts?: BasketOptions) => {
    const shopper = useCustomer(); // In order to determine if we area ready to make basket operations for customer
    const { basket, isInitialized, setInitialized } = useBasketStore(); // get states from Zustand global store for basket
    const operationQueue = BasketOperationQueue.getInstance(); // Use singleton queue

    const uIState = useBasketState();
    const { translate } = useTranslation();
    const { data: frame } = useFrame();
    const { addToBasketEvent, removeFromBasketEvent } = useEvents(frame);
    const { trackAndDisplayError: trackError, trackOnlyError } = useErrorHandler();
    const api = useCommerceAPI();

    // Track initialization
    useEffect(() => {
        if (shopper.isInitialized && basket?.basketId && !isInitialized) {
            setInitialized(true);
        }
    }, [shopper.isInitialized, basket?.basketId, isInitialized, setInitialized]);

    const handleBasketErrors = useCallback(createBasketErrorHandler(trackError), [trackError]);

    const extractCustomProperties = useCallback((item: Record<string, string>) => {
        const properties: Record<string, string> = Object.keys(item).reduce((collection, key) => {
            // Custom keys in Salesforce start with 'c_'.
            if (key.startsWith('c_')) {
                return {
                    ...collection,
                    [key]: item[key],
                };
            }

            return collection;
        }, {});

        return properties;
    }, []);

    const {
        addItemToBasket: baseAddItemToBasket,
        removeItemFromBasket: baseRemoveItemFromBasket,
        updateItemInBasket: baseUpdateItemInBasket,
        approvePaymentInstrument: baseApprovePaymentInstrument,
        updateBasketSuggestedStore: baseSetSuggestedStore,
        loaded,
        ...options
    } = useCommerceBasket({ currency: opts?.currency ?? frame?.market?.currency?.currency });

    const getProductWithCouponApplied = (
        basket: Basket | undefined,
        product: Product | ProductEventData,
    ) => {
        if (!basket) return product;
        const couponCodes = _matchCouponsFromCartWithProducts(
            basket?.productItems,
            basket?.couponItems,
        );

        return {
            ...product,
            coupon: couponCodes?.appliedCoupons[product.id]?.couponCode,
        };
    };

    const increaseItemInBasket = async (product: Product) => {
        if (product.quantity >= product?.c_availableStock) {
            const error: ErrorResponse = {
                type: BasketErrorType.INVALID_QUANTITY,
                message: translate('basket.maxQuantityReached'),
                retryable: false,
            };

            throw error;
        }

        return operationQueue.add(async () => {
            const operation = async () => {
                const result = await baseUpdateItemInBasket(
                    { productId: product.id, quantity: product.quantity + 1 },
                    product.itemId,
                );

                const productWithCouponApplied = getProductWithCouponApplied(result, product);
                addToBasketEvent(_transformProductToDataLayer(productWithCouponApplied, 1));
                return result;
            };

            await handleBasketErrors(operation)();
        });
    };

    const decreaseItemInBasket = async (product: Product) => {
        try {
            const result = await baseUpdateItemInBasket(
                { productId: product.id, quantity: product.quantity - 1 },
                product.itemId,
            );

            const productWithCouponApplied = getProductWithCouponApplied(result, product);
            removeFromBasketEvent(_transformProductToDataLayer(productWithCouponApplied, 1));
            return result;
        } catch (e) {
            throw new Error(translate('basket.removeFromBasketError'));
        }
    };

    const updateItemInBasket = async (
        product: Product,
        newQuantity: number,
        quantityDifference: number,
        type: string,
    ) => {
        try {
            const result = await baseUpdateItemInBasket(
                { productId: product.id, quantity: newQuantity },
                product.itemId,
            );
            const productWithCouponApplied = getProductWithCouponApplied(result, product);
            const basketEvent =
                type === QuantityChangeType.ADD ? addToBasketEvent : removeFromBasketEvent;

            basketEvent(_transformProductToDataLayer(productWithCouponApplied, quantityDifference));
        } catch (e) {
            notifyError(new Error(translate('basket.addToBasketError')));
        }
    };

    const addItemToBasket = async (
        product: ProductEventData,
        variant: Variant,
        newQuantity: number,
        context?: StorePickupOption.ClickAndCollect,
    ): Promise<boolean> => {
        if (!isInitialized) {
            notifyError(new Error('basket.notInitialized'));
            return false;
        }

        if (!variant?.orderable || !newQuantity || !variant.productId) return false;

        const productItems: ProductItemBasket[] = [
            {
                productId: variant.productId,
                quantity: newQuantity,
                ...extractCustomProperties(variant),
            },
        ];

        try {
            const result = await baseAddItemToBasket(productItems);

            const couponCodes = _matchCouponsFromCartWithProducts(
                result?.productItems,
                result?.couponItems,
            );

            const productWithCouponApplied = {
                ...product,
                coupon: couponCodes?.appliedCoupons[product.id]?.couponCode,
            };

            addToBasketEvent(
                getTrackingProduct(productWithCouponApplied, newQuantity),
                undefined,
                context,
            );
            uIState.setAddedProductId(variant.productId);
            return true;
        } catch (error) {
            if (isErrorResponse(error)) {
                const mappedError = mapApiErrorToBasketError(error, translate);
                notifyError(new Error(mappedError.message));
            }

            uIState.toggleMiniBasket(false);
            return false;
        }
    };

    const basketAddItemFromIpaper = async (ipaperData: ProductItemBasket[]) => {
        const result = await baseAddItemToBasket(ipaperData);

        try {
            const products = await api.shopperProducts?.getProducts({
                parameters: {
                    ids: ipaperData.map((item) => item.productId).toString(),
                    currency: frame?.market?.currency?.currency,
                },
            });

            ipaperData.forEach(({ productId, quantity }) => {
                const product = products?.data.find((item) => item.id === productId);

                if (!product) return;

                addToBasketEvent(_transformProductToDataLayer(product, quantity), quantity);
            });
        } catch (error) {
            trackOnlyError(new Error('Failed to track iPaper basket add'), error);
        }

        return result;
    };

    // Add initialization check to other basket operations
    const protectOperation =
        <TArgs extends unknown[], TReturn>(operation: (...args: TArgs) => Promise<TReturn>) =>
        async (...args: TArgs): Promise<TReturn | void> => {
            if (!isInitialized) {
                notifyError(new Error(translate('basket.addToBasketError')));
                return;
            }
            return operation(...args);
        };

    const removeItemFromBasket = async (
        itemId: string,
        product: ProductEventData,
        newQuantity: number,
    ) => {
        return operationQueue.add(async () => {
            try {
                await baseRemoveItemFromBasket(itemId);
                removeFromBasketEvent(getTrackingProduct(product, newQuantity));
            } catch (e) {
                notifyError(new Error(translate('basket.removeFromBasketError')));
                // Don't throw, just notify
            }
        });
    };

    const updatePaymentInstrument = async (
        orderNo: string,
        paymentInstrumentId: string,
        paymentMethodId: string,
    ) => {
        try {
            const order = await baseApprovePaymentInstrument(
                orderNo,
                paymentInstrumentId,
                paymentMethodId,
            );
            if (order?._flash?.length) {
                trackError(
                    'PaymentError: ' +
                        `${(order?._flash[0]?.details?.message, order?._flash[0]?.details)}`,
                );
            }
            return order;
        } catch (e) {
            trackError(e, e);
            throw new Error(translate('basket.removeFromBasketError'));
        }
    };

    const getTrackingProduct = (
        productEventData: ProductEventData,
        quantity: number,
        trackQuantity?: number,
    ) =>
        productEventData.price
            ? {
                  ...productEventData,
                  // Getting product unite price
                  price: parseFloat((productEventData.price / quantity).toFixed(2)),
                  quantity: trackQuantity ?? quantity,
              }
            : {
                  ...productEventData,
                  quantity: trackQuantity ?? quantity,
              };

    return {
        isInitialized,
        handleBasketErrors,
        updatePaymentInstrument,
        updateItemInBasket: protectOperation(updateItemInBasket),
        removeItemFromBasket: protectOperation(removeItemFromBasket),
        addItemToBasket,
        increaseItemInBasket: protectOperation(increaseItemInBasket),
        decreaseItemInBasket: protectOperation(decreaseItemInBasket),
        basketAddItemFromIpaper,
        baseAddItemToBasket,
        setSuggestedStore: baseSetSuggestedStore,
        ...options,
        ...uIState,
        loaded,
    };
};
